import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { FormField, FormSelect } from '../../components/shared/form';
import { useToast } from '../../contexts/ToastContext';
import { useCreateInvitation, teamErrorCode } from '../../hooks/useTeam';
import type { AccountRole, RoleDefinition } from '../../services/team';

/**
 * Invite modal (task 636) — email + role only.
 *
 * No name fields: the invitee sets their own profile at sign-up. The role list
 * and the hint under it come from the AMS role catalog passed in as
 * `assignableRoles` (owner already excluded), so this modal never hardcodes
 * what a role can do. A duplicate-member / duplicate-invite rejection (409) is
 * surfaced inline against the email field, not as a generic failure.
 */

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Account display name, for the title ("Invite to {name}"). */
  accountName: string;
  /** Assignable roles from the catalog (owner filtered out by the caller). */
  assignableRoles: RoleDefinition[];
}

/** Minimal, forgiving email shape check — AMS is the real validator. */
const looksLikeEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export default function InviteMemberModal({
  isOpen,
  onClose,
  accountName,
  assignableRoles,
}: InviteMemberModalProps) {
  const toast = useToast();
  const createInvite = useCreateInvitation();

  const defaultRole = assignableRoles[0]?.role ?? '';
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>(defaultRole);
  const [emailError, setEmailError] = useState<string | undefined>();

  // Re-seed the role when the catalog resolves, and clear the form each open.
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setRole(assignableRoles[0]?.role ?? '');
      setEmailError(undefined);
    }
  }, [isOpen, assignableRoles]);

  const roleHint = useMemo(
    () => assignableRoles.find((r) => r.role === role)?.summary ?? '',
    [assignableRoles, role],
  );

  if (!isOpen) return null;

  const roleOptions = assignableRoles.map((r) => ({ value: r.role, label: r.label ?? r.role }));
  const canSubmit = email.trim().length > 0 && role.length > 0 && !createInvite.isPending;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    if (!looksLikeEmail(email)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(undefined);

    createInvite.mutate(
      { email: email.trim(), role: role as AccountRole },
      {
        onSuccess: (invite) => {
          toast.success('Invite sent', `We emailed ${invite.email} a link to join.`);
          onClose();
        },
        onError: (error) => {
          const code = teamErrorCode(error);
          if (code === 'ALREADY_MEMBER' || code === 'already_member') {
            setEmailError('That person is already a member of this account.');
          } else if (code === 'ALREADY_INVITED' || code === 'already_invited') {
            setEmailError('That email already has a pending invite.');
          } else {
            setEmailError('The invite could not be sent. Please try again.');
          }
        },
      },
    );
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !createInvite.isPending) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-modal-title"
      data-testid="invite-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3
              id="invite-modal-title"
              className="text-lg font-medium text-gray-900 dark:text-white"
            >
              Invite to {accountName}
            </h3>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              They&apos;ll get an email link to join this account.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={createInvite.isPending}
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* noValidate: this modal validates the email itself (looksLikeEmail),
            so the browser's native constraint check must not preempt submit. */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="px-6 py-4 space-y-4">
            <FormField
              label="Email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(undefined);
              }}
              error={emailError}
              required
              placeholder="name@company.com"
              disabled={createInvite.isPending}
            />

            <div>
              <FormSelect
                label="Role"
                name="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                options={roleOptions}
                disabled={createInvite.isPending || roleOptions.length === 0}
                required
              />
              {roleHint && (
                <p
                  className="mt-1.5 text-sm text-gray-500 dark:text-gray-400"
                  data-testid="invite-role-hint"
                >
                  {roleHint}
                </p>
              )}
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-lg flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={createInvite.isPending}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="invite-submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createInvite.isPending ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
